import React, { useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Badge,
  Box,
  Button,
  Divider,
  SwipeableDrawer
} from '@material-ui/core';
import { SidebarNav } from './components';
import { getLocalStorageAuthentication, logout } from '../../../../lib/auth';
import CompanyInfo from '../../../../containers/CompanyInfoContainer';
import UserInfo from '../../../../components/UserInfo';
import { useDispatch, useSelector } from 'react-redux';
import { logoutRequested } from '../../../../redux/features/user/usersSlice';
import { clearUserJobs } from '../../../../redux/features/job/jobsSlice';
import {
  currentFileLogoUrlSelector,
  fileFetchedSelector,
  findFileByUserId
} from '../../../../redux/features/file/filesSlice';
import { useHistory } from 'react-router-dom';
import {
  APP_PATH_INTERVIEWS,
  APP_PATH_JOBS,
  APP_PATH_LOGIN,
  APP_PATH_MESSAGES,
  APP_PATH_MY_APPLICATIONS,
  APP_PATH_PLANS,
  APP_PATH_SUGGESTIONS,
  APP_PATH_SUPPORT,
  APP_VERSION_RELEASE
} from '../../../../config';
import { useCurrentUserInfo } from '../../../../hooks/useCurrentUserInfo';
import {
  fetchUnreadCount,
  totalUnreadCountSelector
} from '../../../../redux/features/messages/messagesSlice';
import websocketService from '../../../../services/websocketService';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import ChatBubbleOutlineOutlinedIcon from '@material-ui/icons/ChatBubbleOutlineOutlined';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import ExitToAppOutlinedIcon from '@material-ui/icons/ExitToAppOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 260,
    [theme.breakpoints.up('lg')]: {
      marginTop: 64,
      height: 'calc(100% - 64px)'
    },
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(3, 2),
    backgroundColor: theme.palette.background.paper
  },
  divider: {
    margin: theme.spacing(3, 0),
    backgroundColor: theme.palette.divider,
    height: '2px'
  },
  nav: {
    marginBottom: theme.spacing(2),
    flex: 1
  },
  companySection: {},
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 'auto',
    textAlign: 'center',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    opacity: 0.6
  },
  supportButton: {
    textTransform: 'none',
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 999
  }
}));

const Sidebar = (props) => {
  const { open, variant, onClose, className, ...rest } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { user, userId } = useCurrentUserInfo();
  const fileLogoUrl = useSelector(currentFileLogoUrlSelector);
  const fileFetched = useSelector(fileFetchedSelector);
  const totalUnreadCount = useSelector(totalUnreadCountSelector);

  // Fetch user's photo/file when user is available
  useEffect(() => {
    if (userId && !fileFetched) {
      console.log('📸 [Sidebar] Fetching user photo for:', userId);
      dispatch(findFileByUserId({ userId, isPublic: false }));
    }
  }, [dispatch, userId, fileFetched]);

  // Fetch unread message count when user is available
  useEffect(() => {
    if (userId) {
      console.log('🔔 [Sidebar] Fetching unread count for user');
      dispatch(fetchUnreadCount());

      // Connect WebSocket for real-time message updates
      const localAuth = getLocalStorageAuthentication();
      if (localAuth?.token && !websocketService.isConnected()) {
        console.log('🔌 [Sidebar] Connecting WebSocket for live updates');
        websocketService.connect(localAuth.token);
      }
    }

    // Cleanup: disconnect WebSocket when component unmounts (user logs out)
    return () => {
      // Note: We don't disconnect here as user might navigate between pages
      // WebSocket will be disconnected on logout via handleLogout
    };
  }, [userId, dispatch, user]);

  const handleLogout = () => {
    // Disconnect WebSocket
    websocketService.disconnect();
    // Clear localStorage
    logout();
    // Clear Redux state
    dispatch(logoutRequested());
    dispatch(clearUserJobs());
    // Redirect to login page
    history.push(APP_PATH_LOGIN);
  };

  // Check if user has ROLE_COMPANY or ROLE_CANDIDATE
  const isCompany = user?.roles?.includes('ROLE_COMPANY');
  const isCandidate = user?.roles?.includes('ROLE_CANDIDATE');

  const pages = [
    // Only show Dashboard for ROLE_COMPANY users (they manage job posts)
    ...(isCompany
      ? [
          {
            title: 'Dashboard',
            href: '/dashboard',
            icon: <DashboardOutlinedIcon />
          }
        ]
      : []),
    // Only show Candidates menu for ROLE_COMPANY users
    ...(isCompany
      ? [
          {
            title: 'Candidates',
            href: '/applications',
            icon: <PeopleAltOutlinedIcon />
          }
        ]
      : []),
    // Only show Interviews menu for ROLE_COMPANY users
    ...(isCompany
      ? [
          {
            title: 'Interviews',
            href: APP_PATH_INTERVIEWS,
            icon: <EventOutlinedIcon />
          }
        ]
      : []),
    // Only show My Applications menu for ROLE_CANDIDATE users
    ...(isCandidate
      ? [
          {
            title: 'Search jobs',
            href: APP_PATH_JOBS,
            icon: <SearchIcon />
          },
          {
            title: 'My Applications',
            href: APP_PATH_MY_APPLICATIONS,
            icon: <AssignmentOutlinedIcon />
          },
          {
            title: 'Suggestions',
            href: APP_PATH_SUGGESTIONS,
            icon: <EmojiObjectsOutlinedIcon />
          }
        ]
      : []),
    // Messages menu for both company and candidate users
    {
      title: 'Messages',
      href: APP_PATH_MESSAGES,
      icon:
        totalUnreadCount > 0 ? (
          <Badge badgeContent={totalUnreadCount} color="error">
            <ChatBubbleOutlineOutlinedIcon />
          </Badge>
        ) : (
          <ChatBubbleOutlineOutlinedIcon />
        )
    },
    // Only show Plan menu for non-candidate users (companies)
    ...(!isCandidate
      ? [
          {
            title: 'Store',
            href: APP_PATH_PLANS,
            icon: <ShoppingCartOutlinedIcon />
          }
        ]
      : []),
    {
      title: 'Settings',
      href: '/settings',
      icon: <SettingsOutlinedIcon />
    },
    {
      title: 'Logout',
      href: '#',
      icon: <ExitToAppOutlinedIcon />,
      onClick: handleLogout
    }
  ];
  return (
    <SwipeableDrawer
      anchor="left"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      onOpen={() => {}}
      open={open}
      variant={variant}>
      <div
        {...rest}
        className={clsx(classes.root, className)}
        onClick={onClose}>
        <div className={classes.companySection}>
          {isCandidate ? (
            <UserInfo user={user} fileLogoUrl={fileLogoUrl} />
          ) : (
            <CompanyInfo />
          )}
        </div>
        <Divider className={classes.divider} />
        <SidebarNav className={classes.nav} pages={pages} />
        {
          // Version of the current application
        }
        <div className={classes.footer}>
          <Box>
            <Button
              size="small"
              variant="outlined"
              className={classes.supportButton}
              startIcon={<ReportProblemOutlinedIcon fontSize="small" />}
              onClick={() => history.push(APP_PATH_SUPPORT)}>
              Support
            </Button>
          </Box>
          <Box>
            <span>Version: {APP_VERSION_RELEASE}</span>
          </Box>
        </div>
      </div>
    </SwipeableDrawer>
  );
};
Sidebar.defaultProps = {
  open: false
};
Sidebar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired
};

export default Sidebar;
